import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import React, { useCallback, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CMSImportCompletedPhase } from '@/components/cms-import/CMSImportCompletedPhase';
import { CMSImportReadyPhase } from '@/components/cms-import/CMSImportReadyPhase';
import { CMSImportStatusBadge } from '@/components/cms-import/CMSImportStatusBadge';
import { CMSImportStepper } from '@/components/cms-import/CMSImportStepper';
import { Button } from '@/components/ui/catalyst/button';
import { cmsImportsApiClient } from '@/services/api/cmsImportsService';

const getCmsTypeLabel = (cmsType: string): string => {
  const labels: Record<string, string> = {
    casepeer: 'CasePeer',
    clio: 'Clio',
    filevine: 'Filevine',
    mycase: 'MyCase',
    neos: 'Neos',
    litify: 'Litify',
    smartadvocate: 'SmartAdvocate',
  };
  return labels[cmsType] || cmsType;
};

export const CMSImportDetailPage: React.FC = () => {
  const { importId } = useParams<{ importId: string }>();

  const {
    data: cmsImport,
    isLoading,
    refetch,
  } = cmsImportsApiClient.useCmsImportRetrieveQuery(
    { id: importId || '' },
    { skip: !importId },
  );

  const [replicate, { isLoading: isReplicating }] =
    cmsImportsApiClient.useCmsImportReplicateMutation();

  // Poll when in replicating or ingesting phase
  const shouldPoll =
    cmsImport?.phase === 'replicating' || cmsImport?.phase === 'ingesting';

  // Callback for when ingest completes (from CMSImportReadyPhase)
  const handleIngestComplete = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!shouldPoll) return undefined;

    const interval = setInterval(() => {
      refetch();
    }, 3000);

    return () => clearInterval(interval);
  }, [shouldPoll, refetch]);

  const handleStartProcessing = async () => {
    if (!importId) return;

    try {
      await replicate({ id: importId }).unwrap();
      toast.success('Processing started');
      refetch();
    } catch (error: unknown) {
      const err = error as { data?: { detail?: string } };
      toast.error(err?.data?.detail || 'Failed to start processing');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-200 border-t-blue-600 dark:border-zinc-700 dark:border-t-blue-400" />
      </div>
    );
  }

  if (!cmsImport) {
    return (
      <div className="flex flex-col gap-8 px-10 py-8">
        <Link
          to="/profile/cms-import"
          className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline w-fit"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Imports
        </Link>
        <div className="p-10 text-center">
          <p className="text-zinc-500 dark:text-zinc-400">
            Import not found or has been deleted.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 px-10 py-8">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <Link
          to="/profile/cms-import"
          className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline w-fit"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Imports
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-4">
              <h1 className="text-4xl font-medium text-black dark:text-white">
                {cmsImport.firm_name}
              </h1>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                {getCmsTypeLabel(cmsImport.cms_type)}
              </span>
              <CMSImportStatusBadge
                phase={cmsImport.error_message ? 'failed' : cmsImport.phase}
              />
            </div>
            <div className="mt-2 flex items-center gap-4 text-sm text-gray-700 dark:text-zinc-300">
              <span>Created by {cmsImport.created_by_name}</span>
              <span>•</span>
              <span>{new Date(cmsImport.created_at).toLocaleString()}</span>
              {cmsImport.file_name && (
                <>
                  <span>•</span>
                  {cmsImport.file_url ? (
                    <button
                      type="button"
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                      onClick={async () => {
                        try {
                          const response = await fetch(cmsImport.file_url!);
                          const blob = await response.blob();
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = cmsImport.file_name;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                        } catch {
                          toast.error('Failed to download file');
                        }
                      }}
                    >
                      {cmsImport.file_name}
                    </button>
                  ) : (
                    <span className="text-zinc-500">{cmsImport.file_name}</span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-gray-200 dark:border-zinc-800 p-6">
        <CMSImportStepper
          phase={cmsImport.phase}
          hasError={!!cmsImport.error_message}
        />
      </div>

      {/* Phase-based Content */}
      <div>
        {/* ERROR state - show prominently when there's an error */}
        {cmsImport.error_message && (
          <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm border border-zinc-200 dark:border-zinc-800 overflow-hidden">
            {/* Subtle red accent bar at top */}
            <div className="h-1 bg-gradient-to-r from-red-500 via-red-400 to-orange-400" />

            <div className="p-10">
              <div className="flex flex-col items-center text-center">
                {/* Icon with subtle glow */}
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-red-500/20 blur-lg scale-125" />
                  <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-lg shadow-red-500/25">
                    <svg
                      className="h-6 w-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                </div>

                <h3 className="mt-5 text-xl font-semibold text-zinc-900 dark:text-white">
                  Import Failed
                </h3>
                <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 max-w-sm">
                  We ran into a problem processing this import.
                </p>

                {/* Error details */}
                <div className="mt-5 w-full max-w-lg rounded-lg bg-red-50 dark:bg-red-950/20 px-4 py-3 ring-1 ring-red-100 dark:ring-red-900/30">
                  <p className="text-sm text-red-700 dark:text-red-300 font-mono break-words text-left">
                    {cmsImport.error_message}
                  </p>
                </div>

                <div className="mt-8">
                  <Link to="/profile/cms-import">
                    <Button color="dark">Back to Imports</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* UPLOADING phase - ready to process */}
        {!cmsImport.error_message && cmsImport.phase === 'uploading' && (
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-gray-200 dark:border-zinc-800 p-8">
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                <svg
                  className="h-6 w-6 text-blue-600 dark:text-blue-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">
                File Uploaded Successfully
              </h3>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 max-w-md mx-auto">
                Your {getCmsTypeLabel(cmsImport.cms_type)} export file has been
                uploaded. Click the button below to start processing the data.
              </p>
              <div className="mt-6">
                <Button
                  color="blue"
                  onClick={handleStartProcessing}
                  disabled={isReplicating}
                >
                  {isReplicating ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Starting...
                    </>
                  ) : (
                    'Start Processing'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* REPLICATING phase - processing in progress */}
        {!cmsImport.error_message && cmsImport.phase === 'replicating' && (
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-gray-200 dark:border-zinc-800 p-8">
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center">
                <svg
                  className="animate-spin h-12 w-12 text-blue-600 dark:text-blue-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">
                Processing Your Data
              </h3>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 max-w-md mx-auto">
                We&apos;re parsing your {getCmsTypeLabel(cmsImport.cms_type)}{' '}
                export and matching cases to matters. This may take a few
                moments...
              </p>
            </div>
          </div>
        )}

        {/* READY phase - single table with two-tier filtering */}
        {!cmsImport.error_message &&
          cmsImport.phase === 'ready' &&
          importId && (
            <CMSImportReadyPhase
              importId={importId}
              firmId={cmsImport.firm_id as string}
              savedFilters={cmsImport.task_filters ?? null}
              onIngestComplete={handleIngestComplete}
            />
          )}

        {/* INGESTING phase - ingestion in progress */}
        {!cmsImport.error_message && cmsImport.phase === 'ingesting' && (
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-gray-200 dark:border-zinc-800 p-8">
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center">
                <svg
                  className="animate-spin h-12 w-12 text-blue-600 dark:text-blue-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">
                Creating External Tasks
              </h3>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 max-w-md mx-auto">
                We&apos;re creating external tasks in Finch from your{' '}
                {getCmsTypeLabel(cmsImport.cms_type)} data. This may take a few
                moments...
              </p>
            </div>
          </div>
        )}

        {/* COMPLETED phase - show summary with tables */}
        {!cmsImport.error_message &&
          cmsImport.phase === 'completed' &&
          importId && (
            <CMSImportCompletedPhase
              importId={importId}
              cmsTypeLabel={getCmsTypeLabel(cmsImport.cms_type)}
            />
          )}
      </div>
    </div>
  );
};
